<template>
    <div class="card-shop" :class="{'card-shop__landingpage': inLandingPage}">
        <router-link v-if="!inLandingPage" :to="`/shops/${shop.id}`" @click.native="handleCardClick">
            <div  class="card-shop__hover-overlay"/>
        </router-link>
        <div v-else class="card-shop__hover-overlay"></div>
        <div @click="dropdownToggle" v-if="dropdownOpened && !inLandingPage" class="overlay"/>
        <div class="card-shop__cover">
            <div class="card-shop__category">
                <p class="card-shop__category__text">#{{ capitalizeCategory(shop.category) }}</p>
            </div>
            <img :src="shop.cover" alt="">
            <div class="card-shop__avatar">
                <img :src="shop.logo" alt="">
            </div>
        </div>
        <div class="card-shop__container">
            <div v-line-clamp="4" class="card-shop__container__body">
                <p class="card-shop__title">
                    {{ shop.title }}
                </p>
                <p class="card-shop__description">
                    {{ shop.description }}
                </p>
            </div>
            <div class="card-shop__container__footer">
                <div v-if="inLandingPage" class="card-shop__container__footer__wrapper-landing">
                    <div class="card-shop__rank">
                        <Icon icon="rank"/>
                        <p class="card-shop__description card-shop__description-inf">
                            {{ shop.alexa_rank | kFormatter(shop.alexa_rank) }}
                        </p>
                    </div>
                    <div class="card-shop__socials">
                        <div class="card-shop__rank">
                            <Icon :icon="`${crosspromSocial[1].platform}shop`"/>
                            <p class="card-shop__description card-shop__description-inf">
                                {{ crosspromSocial[0].followers | kFormatter(crosspromSocial[0].followers) }}
                            </p>
                        </div>
                        <div class="card-shop__rank">
                            <Icon :icon="`${crosspromSocial[0].platform}shop`"/>
                            <p class="card-shop__description card-shop__description-inf">
                                {{ crosspromSocial[1].followers| kFormatter(crosspromSocial[1].followers) }}
                            </p>
                        </div>
                    </div>
                </div>
                <div v-else class="card-shop__container__footer__wrapper">
                    <div :class="{'default-showing': !isOwnersShop}" class="card-shop__aditional-info">
                        <div class="card-shop__rank">
                            <Icon icon="rank"/>
                            <p class="card-shop__description card-shop__description-inf">{{ shop.alexa_rank | kFormatter(shop.alexa_rank) }}</p>
                        </div>
                        <div class="card-shop__match" v-if="!isOwnersShop && matchingScore">
                            <Icon icon="heart"/>
                            <p class="card-shop__description card-shop__description-inf">{{matchingScore}}% Match</p> 
                        </div>
                    </div>
                    <button v-if="!isOwnersShop" @click="showSendMessageModal" class="card-shop__send-message">
                        Send a Message
                    </button>
                    <div class="card-shop__dropdown">
                        <button v-if="isOwnersShop" @click="editShop">
                            <Icon icon="edit-shop"/>
                        </button>
                        <!-- ToDo --- add when the logic will be ready -->
                        <!-- <button v-else @click="dropdownToggle">
                            <Icon icon="more"/>
                        </button> -->
                        <div v-if="dropdownOpened" class="dropdown-items">
                            <ul>
                                <li 
                                    v-for="(item,index) in dropdown" 
                                    :key="item.text"
                                    @click="checkShopShowing(index)"
                                    :class="{'active': item.active}">
                                        <Icon :icon="item.icon"/>
                                        <p class="card-shop__description dropdown-text">{{item.text}}</p>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/components/Theme/Icon'
import {kFormatter} from '../../../core/filters'
import { computed, ref } from '@vue/composition-api'
import { useMatchingScore, useRouter, useStore, useUserId } from '../../../core/hooks'

export default {
    name: 'Card',
    components: {
        Icon
    },
    props: {
        shop: Object,
        cardLocation: String
    },
    setup(props, {root}) {
        const { userId } = useUserId()
        const { route, router } = useRouter()
        const { state, store } = useStore()
        const { monthlySalesMatch, alexaRankMatch, categoryMatch } = useMatchingScore()
        const dropdownOpened = ref(false)
        const dropdown = ref(
            [
                {
                    icon: 'like',
                    text: 'Show more shops like this',
                    active: false
                },
                {
                    icon: 'unlike',
                    text: "Don't show shops like this",
                    active: false
                },
            ]
        )


        const handleCardClick = (e) => {
            root.$ga.event('Shop','View Shop', `
                OpenedFrom: ${props.cardLocation},
                Shop: ${props.shop.title}
            `)
            root.$sendMixpanelEvent('View Shop', {
                'Opened From': props.cardLocation,
                'Shop': props.shop.title
            });
        }


        const dropdownToggle = () => {
            dropdownOpened.value = !dropdownOpened.value
        }
        const checkShopShowing = (index) => {
            dropdownOpened.value = false
            dropdown.value.forEach(item => {
                item.active = false
            });
            this.dropdown[index].active = true
        }
        const editShop = () => {

            root.$sendMixpanelEvent('Edit Shop Clicked', {
                'Shop': props.shop.title
            })

            root.$ga.event('Shop', 'Edit Shop Clicked', `
                Shop: ${props.shop.title}
            `)

            router.push(`/edit-shop/${props.shop.id}`).catch(() => {})
        
        }

        const showSendMessageModal = () => {
            store.commit('TOGGLE_MODAL', ['send-message-modal', {
                openedFrom: props.cardLocation,
                shopData: props.shop
            }])
        }

        const firstShop = state.value.myShops.length && state.value.myShops[0]

        const monthlySalesMatchScore = monthlySalesMatch(firstShop.estimated_monthly_sales, props.shop.estimated_monthly_sales)
        const alexaRankMatchScore = alexaRankMatch(firstShop.alexa_rank, props.shop.alexa_rank)
        const categoryMatchScore = categoryMatch(firstShop.category, props.shop.category) 
        
        const isOwnersShop = computed(() => userId === props.shop.owner_id)
        const matchingScore = computed(() => monthlySalesMatchScore + alexaRankMatchScore + categoryMatchScore)
        const inLandingPage = computed(() => props.cardLocation === 'LandingPage')
        const crosspromSocial = computed(() => props.shop.crossprom_options['social-media-post'].info)
        const capitalizeCategory = (str) => {
            return str.replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase()))).replace('And', 'and')
        }
        return {
            dropdownOpened,
            dropdown,
            isOwnersShop,
            dropdownToggle,
            checkShopShowing,
            editShop,
            handleCardClick,
            matchingScore,
            showSendMessageModal,
            capitalizeCategory,
            inLandingPage,
            crosspromSocial
        }
    },
    filters: {
        kFormatter
    }
}
</script>
<style lang="scss" scoped>
$main-color: #727CF5;
$card-height: 394px;
$card-cover-height: 180px;
$card-color-white: #ffffff;
$card-borders-color: #E2E8F0;
$card-text-color: #333333;
$card-box-shadow: 0px 32px 32px -8px rgba(113, 128, 150, 0.08), 0px 0px 32px -8px rgba(113, 128, 150, 0.12), 0px 0px 1px rgba(113, 128, 150, 0.32); 
$card-border-radius: 8px;
$card-between-spaces: 20px;
$card-title-fontsize: 18px;
$card-description-fontsize: 14px;
$transform: all 0.2s ease;
$family: 'Eina Bold';
$family-semibold: 'Eina SemiBold';

.card-shop {
    display: flex;
    flex-direction: column;
    height: $card-height;
    background: $card-color-white;
    border-radius: $card-border-radius;
    position: relative;
    transform: $transform;
    border: 1px solid $card-borders-color;

    &:hover {
        box-shadow: $card-box-shadow;
    }

    &__landingpage {
        .card-shop__cover { 
            cursor: default;
        }
        
    }

    &__hover-overlay {
        position: absolute;
        width: 100%;
        height: 100%;
        z-index: 1;
        cursor: pointer;
    }
    .overlay {
        position: fixed;
        width: 100%;
        height: 100%;
        z-index: 999;
        top: 0;
        left: 0;
    }
    &__cover {
        height: $card-cover-height;
        position: relative;
        cursor: pointer;
        background: #fff;
        img {
            width: 100%;
            height: 100%;
            border-radius: 8px 8px 0 0;
            object-fit: cover;
        }
    }
    &__avatar {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        position: absolute;
        left: 24px;
        bottom: -18px;
        border: 1px solid $card-color-white;
        background: $card-color-white;
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 50%;
        }
    }
    &__category {
        position: absolute;
        top: 12px;
        right: 16px;
        background: $card-color-white;
        border-radius: $card-border-radius;
        box-shadow: $card-box-shadow;
        display: none;
        align-items: center;
        justify-content: center;
        padding: 4px 12px;
        &__text {
            font-size: 11px;
            font-weight: bold;
            margin: 0;
        }
        
    }
    &__container {
        flex: 1 1 auto;
        padding: 24px 20px;
        display: flex;
        flex-direction: column;
        @media (max-width: 768px) {
            padding: 16px 16px;
        }
        &__body {
            max-height: 95px;
        }
        &__footer {
            flex: 1 1 auto;
            display: flex;            
            align-items: flex-end;
            justify-content: space-between;
            &__wrapper,
            &__wrapper-landing {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding-top: $card-between-spaces;
                border-top: 1px solid $card-borders-color;
                height: 60px;
            }
        }
    }
    &__send-message {
        color: $card-color-white;
        font-size: $card-description-fontsize;
        border-radius: $card-border-radius;
        background: $main-color;
        font-weight: bold;
        padding: 10px 0;
        flex: 1 1 auto;
        display: none;
        transform: $transform;
    }
    &__dropdown {
        position: relative;
        z-index: 2;
        .dropdown-items {
            width: 248px;
            top: 100%;
            right: 0;
            transform: translate(0, 8px);
            position: absolute;
            background: $card-color-white;
            border: 1px solid $card-borders-color;
            border-radius: $card-border-radius;
            z-index: 1000;
            overflow: hidden;
            ul {
                margin: 0;
                padding: 0;
                li {
                    display: flex;
                    align-items: center;
                    padding: 8px 12px;
                    cursor: pointer;
                    .dropdown-text {
                        margin-left: 12px;
                        margin-top: 0;
                    }
                    &:hover {
                        background: #F4F7FA;
                    }
                    &.active {
                        background: #F4F7FA;
                    }
                }
            } 
        }
    }
    &__aditional-info {
        display: flex;
        transform: $transform;
    }
    &__match {
        margin-left: 8px;
    }
    &__socials {
        display: flex;

        .card-shop__rank {
            &:first-child {
                margin: 0 4px;
            }
        }
    }
    &__rank, &__match {
        display: flex;
        padding: 8px 10px;
        border-radius: $card-border-radius;
        border: 1px solid $card-borders-color;
        align-items: center;
    }
    &__title, &__description {
        color: $card-text-color;
        margin: 0;
        word-break: break-word;
    }
    &__title {
        font-family: $family;
        font-size: $card-title-fontsize;
        line-height: 24px;
    }
    &__description {
        font-size: $card-description-fontsize;
        line-height: 20px;
        margin-top: 4px;

    }
    &__description-inf {
        margin-top: 0 !important;
        margin-left: 6px;
        font-family: $family-semibold;
    }
    &:hover {
        .card-shop__category {
            display: flex;
        }
        .card-shop__aditional-info.default-showing {
            display: none;
        }
        .card-shop__send-message {
            display: block;
            z-index: 2;
        }
    }
}
</style>